import { useEffect, useRef, useState } from "react";
import type { WorkOrderId } from "../../lib/workIds";
import {
  type ProcedurePhotoChange,
  useSaveProcedurePhotosMutation,
} from "../../mutations/useSaveProcedurePhotosMutation";
import type { ProcedurePhotoScope } from "../../queries/useProcedurePhotos";
import { useWorkScope } from "../WorkScopeProvider";
import {
  classifyUploadError,
  createPhotoEntry,
  type PhotoUploadEntry,
  revokePreview,
  uploadPhotoEntry,
  validatePhotoSelection,
} from "./procedurePhotoUpload";

type SaveOperation = ProcedurePhotoChange;

/**
 * Owns the photo uploads for one Procedure execution. Files upload one at a
 * time; each verified photo is saved to its item straight away through a
 * single-flight save queue (photos that finish while a save runs are batched
 * into the next one). Nothing runs while `paused` is true, so a manual
 * "Save progress" never races a photo save.
 */
export function useProcedurePhotoQueue({
  workOrderId,
  paused,
}: {
  workOrderId: WorkOrderId;
  paused: boolean;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const saveMutation = useSaveProcedurePhotosMutation(workOrderId);
  const [entries, setEntries] = useState<PhotoUploadEntry[]>([]);
  const [selectionErrors, setSelectionErrors] = useState<
    Record<string, string | null>
  >({});
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const entriesRef = useRef<PhotoUploadEntry[]>([]);
  const saveQueue = useRef<SaveOperation[]>([]);
  const uploading = useRef(false);
  const saving = useRef(false);
  const pausedRef = useRef(paused);

  function commit(next: PhotoUploadEntry[]) {
    entriesRef.current = next;
    setEntries(next);
  }

  function patch(localId: string, change: Partial<PhotoUploadEntry>) {
    commit(
      entriesRef.current.map((entry) =>
        entry.localId === localId ? { ...entry, ...change } : entry,
      ),
    );
  }

  function drop(localIds: string[]) {
    const ids = new Set(localIds);
    for (const entry of entriesRef.current)
      if (ids.has(entry.localId)) revokePreview(entry);
    commit(entriesRef.current.filter((entry) => !ids.has(entry.localId)));
  }

  function scope(): ProcedurePhotoScope {
    if (!selectedCustomer || !selectedFacility)
      throw new Error("A customer and facility must be selected.");
    return {
      customerId: selectedCustomer.id,
      facilityId: selectedFacility.id,
      workOrderId,
    };
  }

  function enqueueSave(operation: SaveOperation) {
    const pending = saveQueue.current.find(
      (queued) =>
        queued.kind === "add" &&
        operation.kind === "add" &&
        queued.itemId === operation.itemId,
    );
    if (pending) pending.fileIds.push(...operation.fileIds);
    else
      saveQueue.current.push({ ...operation, fileIds: [...operation.fileIds] });
    void flushSaves();
  }

  async function runSave(operation: SaveOperation) {
    try {
      const saved = await saveMutation.mutateAsync(operation);
      if (operation.kind === "remove") return;
      const response = saved.responses.find(
        (candidate) => candidate.itemId === operation.itemId,
      );
      const savedIds = new Set(
        response?.value.type === "PHOTO" ? response.value.fileIds : [],
      );
      // A saved photo now renders from the server's signed URL, so its local
      // preview is released.
      drop(
        entriesRef.current
          .filter((entry) => savedIds.has(entry.uploadId))
          .map((entry) => entry.localId),
      );
    } catch (error) {
      if (operation.kind === "remove") return;
      const message =
        error instanceof Error ? error.message : "Unable to save the photo.";
      const failed = new Set(operation.fileIds);
      commit(
        entriesRef.current.map((entry) =>
          failed.has(entry.uploadId)
            ? {
                ...entry,
                phase: "failed",
                // The server names a photo it will no longer accept; only a new
                // upload can replace it.
                error: {
                  message,
                  retry: message.includes(entry.uploadId)
                    ? "new-session"
                    : "save",
                },
              }
            : entry,
        ),
      );
    } finally {
      if (operation.kind === "remove")
        setRemovingIds((current) =>
          current.filter((fileId) => !operation.fileIds.includes(fileId)),
        );
    }
  }

  async function flushSaves() {
    if (saving.current || pausedRef.current) return;
    const operation = saveQueue.current.shift();
    if (!operation) return;
    saving.current = true;
    setIsSaving(true);
    try {
      await runSave(operation);
    } finally {
      saving.current = false;
      setIsSaving(saveQueue.current.length > 0);
      void flushSaves();
    }
  }

  async function processEntry(entry: PhotoUploadEntry) {
    try {
      await uploadPhotoEntry(scope(), entry, (phase, change) =>
        patch(entry.localId, { ...change, phase }),
      );
      // The user removed it while it uploaded; it must not be saved.
      if (
        !entriesRef.current.some((current) => current.localId === entry.localId)
      )
        return;
      patch(entry.localId, { phase: "saving", error: undefined });
      enqueueSave({
        itemId: entry.itemId,
        kind: "add",
        fileIds: [entry.uploadId],
      });
    } catch (error) {
      patch(entry.localId, {
        phase: "failed",
        error: classifyUploadError(error),
      });
    }
  }

  async function pumpUploads() {
    if (uploading.current) return;
    uploading.current = true;
    try {
      while (!pausedRef.current) {
        const next = entriesRef.current.find(
          (entry) => entry.phase === "queued",
        );
        if (!next) break;
        await processEntry(next);
      }
    } finally {
      uploading.current = false;
    }
  }

  // Track the pause flag for the running workers, and resume uploads and
  // pending saves once a manual save finishes.
  useEffect(() => {
    pausedRef.current = paused;
    if (paused) return;
    void pumpUploads();
    void flushSaves();
  });

  // Release every local preview when the Procedure section unmounts.
  useEffect(
    () => () => {
      for (const entry of entriesRef.current) revokePreview(entry);
    },
    [],
  );

  function entriesFor(itemId: string) {
    return entries.filter((entry) => entry.itemId === itemId);
  }

  return {
    entriesFor,
    isSaving,
    removingIds,
    selectionErrorFor: (itemId: string) => selectionErrors[itemId] ?? null,
    /** Queues files for an item; `savedCount` is the item's saved photos. */
    addFiles(itemId: string, files: File[], savedCount: number) {
      const slotsUsed =
        savedCount +
        entriesRef.current.filter((entry) => entry.itemId === itemId).length;
      const { accepted, error } = validatePhotoSelection(files, slotsUsed);
      setSelectionErrors((current) => ({ ...current, [itemId]: error }));
      if (accepted.length === 0) return;
      commit([
        ...entriesRef.current,
        ...accepted.map((file) => createPhotoEntry(itemId, file)),
      ]);
      void pumpUploads();
    },
    retry(localId: string) {
      const entry = entriesRef.current.find(
        (candidate) => candidate.localId === localId,
      );
      if (!entry?.error || entry.error.retry === "none") return;
      if (entry.error.retry === "save") {
        patch(localId, { phase: "saving", error: undefined });
        enqueueSave({
          itemId: entry.itemId,
          kind: "add",
          fileIds: [entry.uploadId],
        });
        return;
      }
      patch(
        localId,
        entry.error.retry === "new-session"
          ? {
              phase: "queued",
              error: undefined,
              uploadId: crypto.randomUUID(),
              staged: false,
            }
          : { phase: "queued", error: undefined },
      );
      void pumpUploads();
    },
    /** Drops an unsaved upload; nothing on the server changes. */
    discard(localId: string) {
      drop([localId]);
    },
    /** Unlinks a saved photo from its item. The stored file is kept. */
    removeSaved(itemId: string, fileId: string) {
      setRemovingIds((current) => [...current, fileId]);
      enqueueSave({ itemId, kind: "remove", fileIds: [fileId] });
    },
  };
}
